import { api } from './index'

/**
 * VRG-265 — resolve a scanned tag to the entity a filter is about.
 *
 * A tag is a physical object; a filter is about an ENTITY, and which entity depends on the field
 * being filtered. Rather than one lookup per field (which is how the previous surface ended up
 * matching Certificates in the browser, calling `owners-by-tags` for Owners, and having no path
 * at all for Product), the server resolves each tag once to everything it reaches and the caller
 * picks by field.
 */
export type ScanFilterEntity = 'certificate' | 'product' | 'owner' | 'tag'

export interface ResolvedTagProduct {
  id: string
  title: string
}

export interface ResolvedTagOwner {
  id: string
  first_name: string
  last_name: string
  email: string
  avatar: string | null
}

export interface ResolvedTagCertificate {
  id: string
  status: string | null
}

export interface ResolvedTag {
  tagId: string
  /**
   * False means this tag has no certificate belonging to this company — a real answer, not an
   * error. It is what lets the UI say "this tag has no certificate yet" instead of adding
   * nothing and letting the operator believe a filter was applied.
   */
  found: boolean
  certificate: ResolvedTagCertificate | null
  product: ResolvedTagProduct | null
  owner: ResolvedTagOwner | null
}

/**
 * The entity a scan resolves to, for a given filter field, plus how to NAME it on screen.
 *
 * Keyed by entity rather than by column so the same map serves the search bar (which filters
 * certificates) and the filter menu (whose `column.field` names the entity directly).
 */
export const ENTITY_LABEL: Record<ScanFilterEntity, string> = {
  certificate: 'certificate',
  product: 'product',
  owner: 'owner',
  tag: 'tag',
}

/** What a resolution contributes to the filter, and what to call it in the UI. */
export interface ResolvedEntity {
  /** The value the filter is keyed on (a certificate / product / owner id). */
  id: string
  /** Human name for the resolved entity, e.g. the product title. */
  label: string
  /** Which entity this is — so the UI can say what it resolved TO, not just show a name. */
  entity: ScanFilterEntity
  /** The tag that produced it, so a row can be removed by the scan the operator made. */
  tagId: string
  /**
   * The resolved entity shaped as the dropdown's own OPTION — the fix for the third failure
   * mode of routing scans into an existing select.
   *
   * These selects are fed a bounded page of rows (the certificates list, `limit: 200` product /
   * company selects). A scanned tag can perfectly well resolve to an entity that is not on that
   * page, and "check the option" then checks nothing at all: the filter would be applied with an
   * id the list cannot name, or — worse — silently not applied. Carrying an option-shaped object
   * lets the field INJECT what it resolved, so the row appears, checked, exactly as if it had
   * been loaded. Shaped to match what each `preview` renderer reads (products: `title`; owners:
   * `first_name`/`last_name`/`email`; certificates: `product.title` + `id`).
   */
  option: Record<string, any>
}

const ownerName = (owner: ResolvedTagOwner): string => {
  const name = [owner.first_name, owner.last_name].filter(Boolean).join(' ').trim()
  return name || owner.email
}

/**
 * Project a resolution onto the entity the surface is filtering by.
 *
 * Returns `null` when the tag resolved but carries nothing for THIS field — an unclaimed
 * certificate on an Owner filter is the real case. That is deliberately distinct from
 * `found: false` (no certificate at all): the two need different sentences, because one is
 * "scan a different tag" and the other is "this item has no owner yet".
 */
export const projectResolution = (
  resolution: ResolvedTag,
  entity: ScanFilterEntity,
): ResolvedEntity | null => {
  // TAG is resolved BEFORE the `found` guard, and that ordering is the point.
  //
  // `found: false` means this tag has no CERTIFICATE for this company — which is exactly the
  // state a freshly encoded, not-yet-assigned tag is in, and precisely the tag an operator on
  // the Tags list is most likely to be holding. For every other entity that is correctly a
  // dead end (no certificate => no product, no owner). For the tag itself it is irrelevant:
  // the scan already produced the tag id, which is the value the filter is keyed on.
  //
  // No server round-trip is needed for this one. `tagId` comes back on every resolution.
  if (entity === 'tag') {
    return {
      id: resolution.tagId,
      label: resolution.tagId,
      entity,
      tagId: resolution.tagId,
      // A tag select lists tags by their id, so the id IS the option. `tag_id` is carried
      // alongside `id` because the two tag surfaces name the column differently.
      option: {id: resolution.tagId, tag_id: resolution.tagId, name: resolution.tagId},
    }
  }

  if (!resolution.found) return null
  if (entity === 'product') {
    return resolution.product
      ? {
          id: resolution.product.id,
          label: resolution.product.title,
          entity,
          tagId: resolution.tagId,
          option: resolution.product,
        }
      : null
  }
  if (entity === 'owner') {
    return resolution.owner
      ? {
          id: resolution.owner.id,
          label: ownerName(resolution.owner),
          entity,
          tagId: resolution.tagId,
          option: resolution.owner,
        }
      : null
  }
  return resolution.certificate
    ? {
        id: resolution.certificate.id,
        // A certificate has no name of its own worth showing here, so name it by the product it
        // carries — which is what the operator is holding — and fall back to the tag id.
        label: resolution.product?.title ?? resolution.tagId,
        entity,
        tagId: resolution.tagId,
        option: {
          ...resolution.certificate,
          product: resolution.product,
          // VRG-286: `resolution.tagId` is a CHIP UID, so it belongs in `tag_uid`. It must NOT
          // be written to `tag_id`, which is now the certificate's uuid FK — putting a UID there
          // would reproduce, on the client, exactly the conflation this change removed from the
          // database. VRG-231 dropped the `e_tag_id` twin that was also set from this value;
          // nothing ever read it back off this synthetic option.
          tag_uid: resolution.tagId,
        },
      }
    : null
}

/**
 * Why a scan contributed nothing, in the operator's terms.
 *
 * The two cases need different sentences because they demand opposite next actions: a tag with
 * no certificate means "this item isn't in Vera yet", while a certificate with no owner means
 * "this item exists but nobody has claimed it". Collapsing them into one message is how an
 * operator ends up re-scanning a tag that was never going to match.
 */
export const scanMissMessage = (
  resolution: ResolvedTag | undefined,
  entity: ScanFilterEntity,
): string => {
  if (!resolution || !resolution.found) {
    return 'This tag has no certificate yet, so nothing was added to the filter.'
  }
  // A certificate with no owner has TWO causes, and "has no owner yet" describes only one
  // of them. If it has not been activated, the brand still holds it — that is the expected
  // pre-sale state, not missing data, and saying so is the difference between an operator
  // re-scanning a tag that was never going to match and one who knows the item is unsold.
  //
  // The brand is deliberately NOT injected into the filter here: `certificates.owner_id`
  // points at the `owners` table, so a company is not a value that filter can take, and
  // applying one would return zero rows.
  //
  // `certificate.status` is what the resolution carries; see `isCertificateActivated` in
  // `utils/certificate.ts` for the rule this mirrors. An unknown status falls through to
  // the generic sentence rather than claiming the brand holds it.
  if (entity === 'owner' && resolution.certificate?.status === 'inactive') {
    return "This tag's certificate hasn't been sold yet, so it has no owner to filter by. Your brand still holds it."
  }
  return `This tag's certificate has no ${ENTITY_LABEL[entity]} yet, so nothing was added to the filter.`
}

export const scanResolveApi = {
  /**
   * Order and duplicates are preserved by the server, so `resolutions[i]` is the answer for
   * `tagIds[i]`. Callers rely on that to attribute a miss to the exact scan that caused it.
   */
  resolveByTags: async (tagIds: string[]): Promise<ResolvedTag[]> => {
    if (!tagIds?.length) return []
    const response = await api.post('/certificates/admin/resolve-by-tags', { tagIds })
    return (response.data?.resolutions ?? []) as ResolvedTag[]
  },
}
